import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Plus, RefreshCw, Search, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { recurrenceLabelKey } from './membership-enums';
import { EditPlanSheet } from './edit-plan-sheet';
import { PlanBenefitsSheet } from './plan-benefits-sheet';
import { useMembershipPlans } from './memberships.api';
import type { MembershipPlan } from './types';

/**
 * Membership plans — the catalogue of what can be sold, not who has bought it.
 *
 * `GET subscriptions` returns everything at once, so the location, status and
 * text filters all run client-side, as they did in the original.
 */

const ANY = 'ANY';

function BoolCell({ value }: { value: boolean }) {
  return value ? (
    <Check className="text-success mx-auto size-4" />
  ) : (
    <X className="text-destructive mx-auto size-4" />
  );
}

export function MembershipPlansPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, isFetching, refetch } =
    useMembershipPlans();

  const [locationName, setLocationName] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [query, setQuery] = useState('');

  const [editing, setEditing] = useState<MembershipPlan | null>(null);
  const [creating, setCreating] = useState(false);
  const [benefitsFor, setBenefitsFor] = useState<MembershipPlan | null>(null);

  const plans = useMemo(() => data ?? [], [data]);

  /*
   * Derived from every plan, not from the filtered list. Deriving from the
   * filtered list meant choosing a location removed every other location from
   * the dropdown, so the filter could only be narrowed, never changed.
   */
  const locationOptions = useMemo(
    () =>
      Array.from(
        new Set(plans.map((plan) => plan.location?.name).filter(Boolean))
      ) as string[],
    [plans]
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return plans.filter((plan) => {
      if (locationName !== ANY && plan.location?.name !== locationName) {
        return false;
      }
      if (status !== ANY && String(Boolean(plan.isActive)) !== status) {
        return false;
      }
      if (!term) return true;
      // The original searched across the location and all three names.
      return [
        plan.location?.name,
        plan.location?.nameEn,
        plan.location?.nameEs,
        plan.nameEn,
        plan.nameEs,
        plan.internalName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [plans, locationName, status, query]);

  const isFiltered = locationName !== ANY || status !== ANY || query !== '';

  const filterFields: FilterField[] = [
    {
      id: 'location',
      label: t('memberships.location'),
      value: locationName,
      onChange: setLocationName,
      options: [
        { value: ANY, label: t('memberships.any') },
        ...locationOptions.map((name) => ({ value: name, label: name })),
      ],
    },
    {
      id: 'status',
      label: t('memberships.isActive'),
      value: status,
      onChange: setStatus,
      // Any / Active / Inactive is an order that means something.
      sort: false,
      options: [
        { value: ANY, label: t('memberships.any') },
        { value: 'true', label: t('memberships.active') },
        { value: 'false', label: t('memberships.inactive') },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('memberships.title')}
        description={t('memberships.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn(isFetching && 'animate-spin')} />
              <span className="hidden sm:inline">{t('common.reload')}</span>
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus />
              {t('memberships.createNew')}
            </Button>
          </div>
        }
      />

      <FilterBar
        busy={isFetching}
        search={
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('memberships.searchPlaceholder')}
              aria-label={t('common.search')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={filterFields}
        summary={
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground text-sm">
              {t('memberships.showingOf', {
                shown: filtered.length,
                total: plans.length,
              })}
            </p>
            {isFiltered && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-7 text-xs"
                onClick={() => {
                  setLocationName(ANY);
                  setStatus(ANY);
                  setQuery('');
                }}
              >
                {t('orders.filters.reset')}
              </Button>
            )}
          </div>
        }
      />

      {isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('memberships.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {plans.length === 0
              ? t('memberships.noPlans')
              : t('memberships.noMatches')}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('memberships.location')}</TableHead>
                  <TableHead>{t('memberships.name')}</TableHead>
                  <TableHead className="text-center">
                    {t('memberships.isActive')}
                  </TableHead>
                  <TableHead>{t('memberships.recurrence')}</TableHead>
                  <TableHead className="text-right">
                    {t('memberships.price')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('memberships.isOnPromo')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('memberships.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>{plan.location?.name ?? '—'}</TableCell>
                    <TableCell>
                      <LocalizedName
                        nameEn={plan.nameEn}
                        nameEs={plan.nameEs}
                        internalName={plan.internalName}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <BoolCell value={plan.isActive} />
                    </TableCell>
                    <TableCell>
                      {t(recurrenceLabelKey(plan.recurrence))}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatCurrency(plan.price)}
                    </TableCell>
                    <TableCell className="text-center">
                      <BoolCell value={plan.isOnPromo} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(plan)}
                        >
                          {t('memberships.edit')}
                        </Button>
                        <Button size="sm" onClick={() => setBenefitsFor(plan)}>
                          {t('memberships.benefits')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={isFetching} />
            <div
              className={cn(
                'space-y-2',
                isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {filtered.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <LocalizedName
                        nameEn={plan.nameEn}
                        nameEs={plan.nameEs}
                        internalName={plan.internalName}
                      />
                      {plan.location?.name && (
                        <p className="text-muted-foreground truncate text-xs">
                          {plan.location.name}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 font-semibold tabular-nums">
                      {formatCurrency(plan.price)}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={plan.isActive ? 'success' : 'secondary'}>
                      {plan.isActive
                        ? t('memberships.active')
                        : t('memberships.inactive')}
                    </Badge>
                    {plan.isOnPromo && (
                      <Badge variant="warning">
                        {t('memberships.isOnPromo')}
                      </Badge>
                    )}
                    <span className="text-muted-foreground text-xs">
                      {t('memberships.recurrence')}:{' '}
                      {t(recurrenceLabelKey(plan.recurrence))}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setEditing(plan)}
                    >
                      {t('memberships.edit')}
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => setBenefitsFor(plan)}
                    >
                      {t('memberships.benefits')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <EditPlanSheet
        plan={editing}
        open={editing !== null || creating}
        onOpenChange={(next) => {
          if (!next) {
            setEditing(null);
            setCreating(false);
          }
        }}
      />

      <PlanBenefitsSheet
        plan={benefitsFor}
        open={benefitsFor !== null}
        onOpenChange={(next) => {
          if (!next) setBenefitsFor(null);
        }}
      />
    </div>
  );
}
